import { match } from 'ts-pattern';

import {
  NonQuotaTargeting,
  QuotaCriterionDto,
  SurveyChannelCreationConfirmedEvent,
  SurveyQuota,
  SurveyQuotaCriteriaPayloadMapper,
} from '@ps/survey-utils';
import { BasePayloadMapper } from '@ps/utils';

import { LaunchTrafficChannelRequestDto } from '../dtos/survey.dto';
import { Option } from '@sapphire/result';

type TcQuotaIdPayload = { id: 'all' | string };
type TcQuotaCriteriaPayload = TcQuotaIdPayload & {
  category: string;
  name: QuotaCriterionDto[];
};
type TcQuotaProgramField = Partial<
  Pick<LaunchTrafficChannelRequestDto, 'aidaQuota' | 'qbpQuota'>
>;

export class LaunchTrafficChannelPayloadMapper extends BasePayloadMapper<
  SurveyChannelCreationConfirmedEvent,
  LaunchTrafficChannelRequestDto
> {
  public toPayload(
    event: SurveyChannelCreationConfirmedEvent,
  ): LaunchTrafficChannelRequestDto {
    const quotaProgramField = this._buildQuotaProgramField(event);

    return match(event)
      .with({ cardType: 'replica' }, (replicaEvent) => ({
        channelSurveyName: replicaEvent.surveyTitle,
        channelType: 'REP',
        payload: {},
        copyTargetingFromParent: true,
        ...quotaProgramField,
      }))
      .with({ cardType: 'targeted-list' }, (targetedListEvent) => ({
        channelSurveyName: targetedListEvent.surveyTitle,
        channelType: 'TL',
        payload: { filePath: targetedListEvent.filePath },
        copyTargetingFromParent: false,
        ...quotaProgramField,
      }))
      .exhaustive();
  }

  private _buildQuotaProgramField(
    event: SurveyChannelCreationConfirmedEvent,
  ): TcQuotaProgramField {
    return Option.all([event.quotaProgram, event.selectedQuota]).mapOr(
      {},
      ([program, quota]) => ({
        [`${program}Quota`]: this._mapQuotaPayload(quota),
      }),
    );
  }

  private _mapQuotaPayload(
    quota: SurveyQuota | 'all',
  ): TcQuotaCriteriaPayload | TcQuotaIdPayload {
    if (quota === 'all') {
      return { id: 'all' };
    }

    const nonQuotaTargeting = NonQuotaTargeting.from([
      new Set(quota.criteria.map((criterion) => criterion.qualificationCode)),
    ]);
    const criterionPayloadMapper = new SurveyQuotaCriteriaPayloadMapper(
      nonQuotaTargeting,
    );
    return {
      id: quota.id,
      category: quota.label,
      name: criterionPayloadMapper.toPayload(quota.criteria),
    };
  }
}
